import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "motorbike-puncture-repair")!;

function MotorbikePunctureGuide() {
    usePageMeta({
        title:
            "How to fix a tyre puncture on a motorbike | Planamoto",
        description:
            "The roadside drill for a punctured motorbike tyre — telling tubeless from tubed, plugging a nail hole in ten minutes, reinflating with a pump or CO2, and knowing when it's a recovery job rather than a repair.",
        url: "https://www.planamoto.com/guides/motorbike-puncture-repair",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="How to fix a tyre puncture on a motorbike"
            lede="A nail through the tread sounds like the end of a ride. On most modern bikes it's a ten-minute stop — if you carry a plug kit, know whether your tyre is tubeless or tubed, and have run the drill once at home before you ever needed it in the rain."
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                description: META.summary,
            }}
        >
            <GuideStackLead
                eyebrow="The short version"
                authorNote="Spencer · what actually gets you rolling again"
                heading="A puncture isn't the end of the day"
                intro="Almost every puncture is a nail or a screw picked up in the tread, and on a tubeless tyre that's a roadside fix, not a rescue. Tubed tyres are harder and usually a call to recovery. The one thing that decides which drill you're in is something you should know before you leave the drive — not while you're kneeling on a hard shoulder."
                columns={[
                    {
                        label: "01 · Tubeless",
                        title: "Plug it and ride on",
                        body: "Ream the hole, push in a sticky plug, put the air back. The common puncture — an object in the tread — becomes a short stop. Most cast-wheel bikes are tubeless.",
                    },
                    {
                        label: "02 · Tubed",
                        title: "Harder — usually a recovery",
                        body: "A tube means levering the tyre off the rim to patch or swap it. Doable with practice, rarely worth it at the roadside. Traditional spoked wheels are usually tubed.",
                    },
                    {
                        label: "03 · Sidewall or slash",
                        title: "Don't repair — recover",
                        body: "A split sidewall, a gash, or a hole a plug won't fill is not a fix. A plugged sidewall is a blow-out waiting to happen. Get it recovered and replaced.",
                    },
                ]}
                backstop="the breakdown-cover number saved where you can reach it in gloves. European cover — RAC, ADAC, or your insurer's — turns a tyre you can't fix into a lift to a garage instead of a night at the roadside."
            />
            <div className="gt-guide-transition">
                <div className="gt-eyebrow gt-guide-transition__eyebrow">
                    The full drill
                </div>
                <h2 className="gt-guide-transition__heading">
                    Know your tyre, carry the kit, run the steps.
                </h2>
                <p className="gt-guide-transition__intro">
                    What to check before you go, the kit that earns its place, the
                    tubeless fix step by step, and the punctures you should never try
                    to repair.
                </p>
            </div>

            <GuideSection
                index={1}
                heading="First — know which tyre you're riding on"
                intro="This is the check that matters, and it's a two-minute job at home rather than a guess on the roadside. It decides whether a puncture is a stop or a recovery, and what kit is worth carrying."
                items={[
                    {
                        title: "Read the sidewall",
                        body: "It's moulded right there: TUBELESS or TUBE TYPE. If it says TUBE TYPE — or you genuinely can't tell — treat it as tubed and plan for the harder case.",
                    },
                    {
                        title: "Cast wheels usually mean tubeless",
                        body: "Solid cast-alloy wheels hold air at the rim, so nearly all of them run tubeless. Traditional wire-spoked wheels leak through the spoke holes and normally need a tube — unless they're the sealed tubeless-spoked type fitted to some adventure bikes.",
                    },
                    {
                        title: "Check both ends before a trip",
                        body: "Front and rear can differ, and a valve that's a straight metal stem is another tubeless clue. Know the answer for your bike now — write it on the same sheet as your breakdown number.",
                    },
                ]}
            />

            <GuideSection
                index={2}
                heading="The kit that turns a puncture into a stop"
                intro="Small, cheap, and it lives under the seat all season. Buy a decent plug kit rather than the cheapest, and — this is the part people skip — practise the drill once at home, in daylight, before you need it for real."
                items={[
                    {
                        title: "A tubeless plug kit",
                        body: "Sticky rope plugs, a reaming tool to rough up the hole, and an insertion tool. The single most useful thing you can carry: a nail becomes a ten-minute fix instead of a recovery truck.",
                    },
                    {
                        title: "A way to put the air back",
                        body: "A compact 12V pump off the battery, or a couple of CO2 canisters for speed. A plug is useless if you can't reinflate. The pump is slower but never runs out; CO2 is fast but you get two or three shots. Carrying both covers you.",
                    },
                    {
                        title: "Pliers to pull the object",
                        body: "A multi-tool or a small pair of pliers to grip and remove the nail or screw. Your fingers won't shift one that's flush with the tread.",
                    },
                    {
                        title: "A pressure gauge",
                        body: "Cheap and pairs with the plug kit. TPMS won't set a cold pressure with the engine off, and a plug that's holding still needs the right pressure to be safe.",
                    },
                    {
                        title: "Nitrile gloves and a rag",
                        body: "Keeps road grime and tyre gunk off your riding gloves and out of the hotel sink. A small dignity that saves the rest of the day.",
                    },
                ]}
            />

            <GuideSection
                index={3}
                columns={[
                    {
                        heading: "The tubeless fix — step by step",
                        items: [
                            {
                                marker: "1",
                                title: "Get safe, then find it",
                                body: "Pull well off the road and get the bike upright and stable. Look and listen for the object or the hiss; if you can't spot it, a squirt of water bubbles at the hole.",
                            },
                            {
                                marker: "2",
                                title: "Pull it and ream",
                                body: "Grip the nail with the pliers and pull it out. Work the reaming tool in and out of the hole a few times to clean and roughen it — the plug grips far better on a reamed hole.",
                            },
                            {
                                marker: "3",
                                title: "Plug it",
                                body: "Thread a sticky plug through the eye of the inserter, push it into the hole until only a few millimetres show, then pull the tool straight out — the plug stays behind. Trim the tail flush with the tread.",
                            },
                            {
                                marker: "4",
                                title: "Reinflate and test",
                                body: "Pump or CO2 back up to the bike's spec pressure. Wet the plug — no bubbles means it's holding. Give it a minute and check the gauge hasn't dropped.",
                            },
                            {
                                marker: "5",
                                title: "Ride gently, get it checked",
                                body: "A plug is a get-you-home, not a permanent repair. Ride steady, ease off hard braking and cornering, and book the tyre in for a proper inside patch or a replacement soon.",
                            },
                        ],
                    },
                    {
                        heading: "Before you ride off",
                        items: [
                            {
                                marker: "✓",
                                title: "Confirm it's holding",
                                body: "Watch the gauge for a few minutes. A pressure that keeps creeping down means the plug hasn't sealed — try a second plug in the same hole, or it's a recovery job.",
                            },
                            {
                                marker: "✓",
                                title: "Drop your pace",
                                body: "Treat a plugged tyre as temporary. Lower speed, gentle inputs, and give yourself extra distance — especially in the first few miles while you're sure of it.",
                            },
                            {
                                marker: "✓",
                                title: "Book the real fix",
                                body: "Plan your route to a tyre shop, not just onward. A rope plug can last, but the safe repair is a mushroom patch from inside the tyre or a new tyre entirely.",
                            },
                        ],
                    },
                ]}
            />

            <GuideSection
                index={4}
                heading="Tubed tyres — why it's the harder call"
                intro="On a tubed tyre the air is in the tube, not sealed at the rim, so a plug does nothing. Fixing it means getting the tyre off — and that's a different afternoon."
                items={[
                    {
                        title: "It means breaking the tyre off the rim",
                        body: "You'd need tyre levers, the strength and technique to unseat a stiff bead at the roadside, and either a patch for the tube or a spare tube to fit. It's doable, but it's slow, sweaty work that beats most people without a workshop.",
                    },
                    {
                        title: "Most riders make the recovery call",
                        body: "For a day ride or a European tour with phone signal, a tubed puncture is usually where breakdown cover earns its keep. The time and risk of a roadside tube change rarely beats a van to the nearest garage.",
                    },
                    {
                        title: "Going remote? Carry a tube and practise",
                        body: "On a trail or overland trip far from recovery, carry the right spare tube, quality levers, and — the crucial bit — change one on your own driveway first. A tube swap you've never done is not a skill you have.",
                    },
                    {
                        title: "A tubeless repair rope won't save a tube",
                        body: "Don't waste time plugging a tubed tyre; the plug seals the casing but the tube inside is still holed. Diagnose the tyre type first so you're running the right drill.",
                    },
                ]}
            />

            <GuideSection
                index={5}
                heading="When a puncture is a recovery job, not a repair"
                intro="Some punctures should never be ridden on, whatever kit you're carrying. Know these before you're tempted to bodge one to get moving:"
                highlight={{
                    glyph: "★",
                    background: "var(--gt-accent)",
                    color: "#3a2a0a",
                }}
                items={[
                    {
                        title: "Sidewall damage — never plug it",
                        body: "The sidewall flexes constantly and carries the load. A plug there won't hold and can let go without warning. A holed or split sidewall is a new tyre, full stop.",
                    },
                    {
                        title: "A hole too big, or too many",
                        body: "Plugs are for small tread punctures. A gash, a hole wider than a plug will fill, or several close together is beyond a roadside fix — recover it.",
                    },
                    {
                        title: "A tube with no kit or no experience",
                        body: "If it's tubed and you don't have the tube, levers, and the practice to use them, don't start. Make the call and stay safe on the verge.",
                    },
                    {
                        title: "Any doubt it'll hold at speed",
                        body: "If the plug keeps weeping, the pressure won't hold, or something just feels wrong, believe it. A tyre letting go at motorway speed is not a risk worth taking to save an hour.",
                    },
                    {
                        title: "Breakdown cover is the real tool",
                        body: "For anything you can't safely fix, the most useful thing on the bike is the recovery number. It turns a trip-ending puncture into a long lunch and a lift to a garage.",
                    },
                ]}
            />

            <GuideCtaBand
                heading="Pack the plug kit before you pack anything else."
                body="The puncture repair kit, the pump and the breakdown number live in Planamoto's shared packing checklist — so they make it into the panniers before you leave, every trip, for everyone in the group."
            />

            <RelatedGuides
                slugs={[
                    "motorbike-tool-kit",
                    "packing-europe",
                    "bike-security",
                    "motorbike-navigation-tools",
                    "fuel-prices",
                ]}
            />

            <CtaCard
                title="Fix the flat — we'll plan the rest"
                body="Stops, overnight addresses, expenses across currencies and a shared checklist for the kit above — the plan around the ride lives in Planamoto. Browse a curated tour to see a day laid out, or start your own."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default MotorbikePunctureGuide;
